// ---------------------------------------------------------------------------
// Op log — pure types + transitions for the client-side intent queue.
//
// Every user write (createSession, send, steer, edit, delete) is an
// OpEntry. A serialized pump drains pending → inflight → acked|failed.
// The store holds the ops array; this module never touches React or the
// network.
// ---------------------------------------------------------------------------

import type { SessionNavigation } from "./threads";

export type OpState = "pending" | "inflight" | "acked" | "failed";

export type OpDeliveryMode = "queue" | "steer";

export const OP_MODEL_MODES = ["fast", "deep", "fusion"] as const;

export type OpModelMode = (typeof OP_MODEL_MODES)[number];

export const OP_RETENTION_ARMS = ["zdr", "zdr_opt_out"] as const;

/** Data-retention arm an official mode resolves from; the org's own arm applies when omitted. */
export type OpRetentionArm = (typeof OP_RETENTION_ARMS)[number];

export interface OpAttachment {
  type: "image" | "file";
  base64?: string;
  mediaType: string;
  fileName?: string;
}

export interface CreateSessionOpPayload {
  /** Client-minted session UUID — also the op-log bucket key. */
  sessionId: string;
  installationModelId: number;
  title?: string;
  enableAutofix?: boolean;
  prRepository?: string;
  prNumber?: number;
  sourceOrigin?: "pr_comment" | "onboarding";
  monitorDebug?: {
    monitor_rule_id?: string;
    monitor_alert_ids?: string[];
    retention_ids?: string[];
  };
  /** Automation the session edits ("Edit with [code]smith"). */
  automationId?: string;
  clientSubmitStartedAtMs?: number;
  /** Client-only viewport behavior; omitted from the create-session wire body. */
  navigation: SessionNavigation;
  /**
   * Session created speculatively while the user was still typing the first
   * prompt (see useSessionPrewarm). Stays out of the sidebar pools until a
   * send adopts it.
   */
  prewarm?: boolean;
}

export interface SendOpPayload {
  content: string;
  attachments?: OpAttachment[];
  /**
   * Context that rides the dispatched query server-side (e.g. the rightsize
   * card selection) but is never part of the visible message content.
   */
  hiddenContext?: string;
  replyToMessageId?: string;
  /** Client-generated pair UUIDs the server persists identically. */
  userMessageId: string;
  draftMessageId?: string;
  /** True when the send entered the queue behind a busy thread (a queued draft row exists). */
  queued?: boolean;
  deliveryMode: OpDeliveryMode;
  /** Model switcher selection; omitted when the switcher is hidden. */
  modelMode?: OpModelMode;
  /** Internal catalog preset (superusers) dispatched for this turn instead of a mode. */
  modelPreset?: string;
  /** Retention arm override (superusers) the mode resolves from for this turn. */
  retention?: OpRetentionArm;
  clientSubmitStartedAtMs: number;
}

export interface SteerOpPayload {
  draftMessageId: string;
}

export interface EditQueuedOpPayload {
  messageId: string;
  content: string;
  attachments?: OpAttachment[];
}

export interface DeleteQueuedOpPayload {
  draftMessageId: string;
}

export type OpPayload =
  | CreateSessionOpPayload
  | SendOpPayload
  | SteerOpPayload
  | EditQueuedOpPayload
  | DeleteQueuedOpPayload;

interface OpEntryBase {
  id: string;
  clientSeq: number;
  state: OpState;
  attempts: number;
  lastError?: string;
}

export type OpEntry =
  | (OpEntryBase & { type: "createSession"; payload: CreateSessionOpPayload })
  | (OpEntryBase & { type: "send"; payload: SendOpPayload })
  | (OpEntryBase & { type: "steer"; payload: SteerOpPayload })
  | (OpEntryBase & { type: "editQueued"; payload: EditQueuedOpPayload })
  | (OpEntryBase & { type: "deleteQueued"; payload: DeleteQueuedOpPayload });

export function nextClientSeq(ops: readonly OpEntry[]): number {
  let max = 0;
  for (const op of ops) {
    if (op.clientSeq > max) max = op.clientSeq;
  }
  return max + 1;
}

export function enqueueOp(ops: OpEntry[], entry: OpEntry): OpEntry[] {
  return [...ops, entry];
}

export function markOpInflight(ops: OpEntry[], opId: string): OpEntry[] {
  return ops.map((op) =>
    op.id === opId
      ? { ...op, state: "inflight" as const, attempts: op.attempts + 1 }
      : op,
  );
}

/** Prune an acked op from the log after the store has handed the pair off. */
export function markOpAcked(ops: OpEntry[], opId: string): OpEntry[] {
  return ops.filter((op) => op.id !== opId);
}

export function markOpFailed(
  ops: OpEntry[],
  opId: string,
  error: string,
): OpEntry[] {
  return ops.map((op) =>
    op.id === opId ? { ...op, state: "failed" as const, lastError: error } : op,
  );
}

/** Move a failed op back to pending at the end of the log (FIFO among retries). */
export function retryOp(ops: OpEntry[], opId: string): OpEntry[] {
  const target = ops.find((op) => op.id === opId);
  if (!target || target.state !== "failed") return ops;
  const without = ops.filter((op) => op.id !== opId);
  return [
    ...without,
    {
      ...target,
      state: "pending" as const,
      lastError: undefined,
      attempts: 0,
      clientSeq: nextClientSeq(without),
    },
  ];
}

/**
 * Re-queue an inflight op for one automatic network retry without resetting
 * `attempts` (so the next failure is not retried again).
 */
export function requeueOpForAutoRetry(ops: OpEntry[], opId: string): OpEntry[] {
  return ops.map((op) =>
    op.id === opId
      ? { ...op, state: "pending" as const, lastError: undefined }
      : op,
  );
}

export function discardOp(ops: OpEntry[], opId: string): OpEntry[] {
  return ops.filter((op) => op.id !== opId);
}

export function rewriteSendOpPayload(
  ops: OpEntry[],
  opId: string,
  rewrite: (payload: SendOpPayload) => SendOpPayload,
): OpEntry[] {
  return ops.map((op) => {
    if (op.id !== opId || op.type !== "send") return op;
    return { ...op, payload: rewrite(op.payload) };
  });
}

/** The websocket must stay closed until createSession successfully acks. */
export function hasUnresolvedCreateSession(ops: readonly OpEntry[]): boolean {
  return ops.some(
    (op) =>
      op.type === "createSession" &&
      (op.state === "pending" ||
        op.state === "inflight" ||
        op.state === "failed"),
  );
}

/**
 * Lowest-clientSeq pending op, or null if the log is empty / an op is
 * already in flight (serialized drain).
 *
 * A createSession that is still pending/inflight/failed blocks every other
 * op: the session id is not usable on the wire until create acks.
 */
export function headPendingOp(ops: readonly OpEntry[]): OpEntry | null {
  if (ops.some((op) => op.state === "inflight")) return null;
  const createUnresolved = ops.some(
    (op) =>
      op.type === "createSession" &&
      (op.state === "pending" ||
        op.state === "inflight" ||
        op.state === "failed"),
  );
  let head: OpEntry | null = null;
  for (const op of ops) {
    if (op.state !== "pending") continue;
    if (createUnresolved && op.type !== "createSession") continue;
    if (head == null || op.clientSeq < head.clientSeq) head = op;
  }
  return head;
}

export function sendOpForUserOrDraft(
  ops: readonly OpEntry[],
  messageId: string,
): Extract<OpEntry, { type: "send" }> | null {
  for (const op of ops) {
    if (op.type !== "send") continue;
    if (
      op.payload.userMessageId === messageId ||
      op.payload.draftMessageId === messageId
    ) {
      return op;
    }
  }
  return null;
}

/**
 * Every send mints a draftMessageId for wire attribution, so its presence no
 * longer implies a queued draft row. Ops persisted before `queued` existed
 * carried a draftMessageId only when queued, hence the fallback.
 */
export function sendOpWasQueued(payload: SendOpPayload): boolean {
  return payload.queued ?? Boolean(payload.draftMessageId);
}

export function createCreateSessionOpEntry(args: {
  id?: string;
  clientSeq: number;
  payload: CreateSessionOpPayload;
}): OpEntry {
  return {
    id: args.id ?? crypto.randomUUID(),
    clientSeq: args.clientSeq,
    type: "createSession",
    payload: args.payload,
    state: "pending",
    attempts: 0,
  };
}

export function createSendOpEntry(args: {
  id?: string;
  clientSeq: number;
  payload: SendOpPayload;
}): OpEntry {
  return {
    id: args.id ?? crypto.randomUUID(),
    clientSeq: args.clientSeq,
    type: "send",
    payload: args.payload,
    state: "pending",
    attempts: 0,
  };
}

export function createSteerOpEntry(args: {
  id?: string;
  clientSeq: number;
  draftMessageId: string;
}): OpEntry {
  return {
    id: args.id ?? crypto.randomUUID(),
    clientSeq: args.clientSeq,
    type: "steer",
    payload: { draftMessageId: args.draftMessageId },
    state: "pending",
    attempts: 0,
  };
}

export function createEditQueuedOpEntry(args: {
  id?: string;
  clientSeq: number;
  payload: EditQueuedOpPayload;
}): OpEntry {
  return {
    id: args.id ?? crypto.randomUUID(),
    clientSeq: args.clientSeq,
    type: "editQueued",
    payload: args.payload,
    state: "pending",
    attempts: 0,
  };
}

export function createDeleteQueuedOpEntry(args: {
  id?: string;
  clientSeq: number;
  draftMessageId: string;
}): OpEntry {
  return {
    id: args.id ?? crypto.randomUUID(),
    clientSeq: args.clientSeq,
    type: "deleteQueued",
    payload: { draftMessageId: args.draftMessageId },
    state: "pending",
    attempts: 0,
  };
}

/**
 * Pending and failed ops, plus inflight createSession/send ops. Other
 * inflight writes are dropped mid-refresh rather than re-posted, but
 * createSession and send are idempotent (client-minted ids → server
 * replays). Dropping an inflight send after create has already acked
 * would leave an empty session with nothing to resume on reload.
 */
export function persistableOps(ops: readonly OpEntry[]): OpEntry[] {
  return ops.filter(
    (op) =>
      op.state === "pending" ||
      op.state === "failed" ||
      (op.state === "inflight" &&
        (op.type === "createSession" || op.type === "send")),
  );
}

export function rehydrateOps(ops: readonly OpEntry[]): OpEntry[] {
  return ops.map((op) =>
    op.state === "inflight" ? { ...op, state: "pending" as const } : op,
  );
}
